import { BANDS, type BandId } from '@/domain/bands';
import { coverageColor, modelLabel } from '@/domain/bikeModels';
import type { ModelReach } from '@/hooks/useLookup';
import type { Dictionary } from '@/i18n';

type BandLadderProps = {
  dict: Dictionary;
  band: BandId;
  /* How far each model actually is. The rung the lead sits on reads the closest station of
     any kind, so on its own it can promise a network their own bike cannot reach. */
  reach: readonly ModelReach[];
};

const ORDER: readonly BandId[] = ['excellent', 'covered', 'limited', 'far'];

const FILL = {
  excellent: 'bg-green-500',
  covered: 'bg-blue-500',
  limited: 'bg-yellow-500',
  far: 'bg-red-500',
  out_of_region: 'bg-grey-800',
} satisfies Record<BandId, string>;

const SEGMENT_LABEL = {
  excellent: `0–${BANDS.coveredFromKm}`,
  covered: `${BANDS.coveredFromKm}–${BANDS.limitedFromKm}`,
  limited: `${BANDS.limitedFromKm}–${BANDS.farFromKm}`,
  far: `${BANDS.farFromKm}+`,
  out_of_region: '',
} satisfies Record<BandId, string>;

/* The ladder ends at "20+" and out_of_region has no rung of its own, so a model that far
   lands on the last one. "20+" is true of 84 km, and the accessible label still says the
   station is outside the service area rather than rounding it off. */
const segmentIndex = (band: BandId): number =>
  band === 'out_of_region' ? ORDER.length - 1 : ORDER.indexOf(band);

const rangeText = (band: BandId, dict: Dictionary): string => {
  if (band === 'excellent') {
    return dict.ladder.upTo(BANDS.coveredFromKm);
  }
  if (band === 'covered') {
    return dict.ladder.between(BANDS.coveredFromKm, BANDS.limitedFromKm);
  }
  if (band === 'limited') {
    return dict.ladder.between(BANDS.limitedFromKm, BANDS.farFromKm);
  }
  if (band === 'far') {
    return dict.ladder.above(BANDS.farFromKm);
  }
  return dict.ladder.outOfRegion;
};

/* The ladder is one image to a screen reader, so nothing inside it is announced: the arrow,
   its colour and its position all stop at the eye. The label has to name the fact, not put
   a distance next to a model name and leave the noun out. It is also the only place that
   still says a model is outside the service area, which the "20+" rung rounds off. */
const accessibleLabel = (dict: Dictionary, band: BandId, reach: readonly ModelReach[]): string =>
  [
    dict.ladder.ariaLabel(rangeText(band, dict)),
    ...reach.map((entry) =>
      dict.ladder.modelReach(modelLabel(entry.model), rangeText(entry.band, dict)),
    ),
  ].join('. ');

/* The ladder, not the kilometre reading, is the honest answer. It shows the four
   bands and where this lead sits, so the boundaries the whole company uses are
   visible instead of implied by a single number.

   Under each rung, an arrow per model marks where a lead can actually swap their own bike.
   The kilometre scale reads above the bar so the arrow can sit against the rung it points
   at; below the label it was 40px adrift and read as a footnote.

   One arrow per model, each in its model colour, and never one merged arrow: yellow means
   a single STATION serves both models, while two models reaching the same band can be two
   single-model stations with no yellow dot anywhere near. Per model the colour holds — a
   Comfort arrow always points at a station that takes a Comfort — and the name is written
   beside it, so colour carries nothing on its own.

   One model per line, not joined by a separator. A 360px phone gives each rung 65px and
   "Comfort · CPX" needs 72px, so the joined form wrapped with the separator left dangling
   at the end of the line, on the case most leads see. */
export const BandLadder = ({ dict, band, reach }: BandLadderProps) => {
  const activeIndex = ORDER.indexOf(band);

  return (
    <div
      role="img"
      aria-label={accessibleLabel(dict, band, reach)}
      className="flex items-start gap-1.5"
    >
      {ORDER.map((segment, index) => {
        const isActive = index === activeIndex;
        const models = reach
          .filter((entry) => segmentIndex(entry.band) === index)
          .map((entry) => entry.model);

        return (
          <div key={segment} data-band={segment} className="flex min-w-0 flex-1 flex-col gap-1">
            <span
              data-scale=""
              aria-hidden="true"
              className={
                isActive
                  ? 'text-[11px] font-extrabold tabular-nums text-black'
                  : 'text-[11px] font-medium tabular-nums text-grey-600'
              }
            >
              {SEGMENT_LABEL[segment]} km
            </span>
            <span
              data-bar=""
              className={
                isActive
                  ? `animate-ladder-fill h-1.5 rounded-full ${FILL[band]}`
                  : 'h-1.5 rounded-full bg-grey-200'
              }
              aria-hidden="true"
            />
            {models.length === 0 ? null : (
              <div data-mark={segment} aria-hidden="true" className="flex flex-col gap-0.5">
                {models.map((model) => (
                  <span
                    key={model}
                    data-model={model}
                    className="flex items-start gap-1 text-[10px] font-bold leading-tight text-black"
                  >
                    <span
                      className="mt-0.5 h-0 w-0 shrink-0 border-x-[3px] border-b-[5px] border-x-transparent"
                      style={{ borderBottomColor: coverageColor(model) }}
                    />
                    {modelLabel(model)}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
